import { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'formik';
import { Building, Route, Search, X } from 'lucide-react';

import { CourtierListItem } from 'api/courtiers';
import { courtiersAPI } from 'services/swagger-api';

import { getMandantsTunnels } from './mandantsApi';
import {
  MandantSelection,
  MandantSelectorPlateforme,
  MandantTunnelChoice,
} from './mandantTypes';

interface MandantsTunnelsSelectorProps {
  /** Nom du champ Formik (MandantSelection[] : {courtierId, tunnelIds[]}). */
  name: string;
  /**
   * Plateformes de rattachement du deviseur : les mandants proposés sont les
   * courtiers de ces plateformes, groupés par plateforme. Tant qu'aucune
   * n'est choisie, le sélecteur est verrouillé.
   */
  plateformes: MandantSelectorPlateforme[];
  /** Libellés connus des mandants déjà sélectionnés (mode édition). */
  initialLabels?: Record<string, string>;
}

/**
 * Sélecteur GÉNÉRIQUE des courtiers mandants d'un deviseur mandataire (MIA,
 * professionnel de l'immobilier…) : choix des courtiers (recherche serveur
 * groupée par plateforme) PUIS, pour chaque mandant retenu, choix des tunnels
 * accordés via lui. Granularité paire (mandant, tunnel) : le même tunnel coché
 * chez un mandant ne l'est pas automatiquement chez un autre. Le backend
 * re-valide la couverture (tunnel ⊆ habilitations actives du mandant).
 */
export const MandantsTunnelsSelector: React.FC<MandantsTunnelsSelectorProps> = ({
  name,
  plateformes,
  initialLabels,
}) => {
  const [field, meta, helpers] = useField<MandantSelection[]>(name);
  const [search, setSearch] = useState('');
  const [resultsByOrg, setResultsByOrg] = useState<Record<string, CourtierListItem[]>>({});
  const [selectedLabels, setSelectedLabels] = useState<Record<string, string>>(initialLabels ?? {});
  const [tunnelsByCourtier, setTunnelsByCourtier] = useState<Record<string, MandantTunnelChoice[]>>({});
  const [loading, setLoading] = useState(false);
  const [loadingTunnels, setLoadingTunnels] = useState(false);

  const selections = useMemo(() => field.value ?? [], [field.value]);
  const selectedIds = useMemo(() => selections.map((s) => s.courtierId), [selections]);

  // Clés stables pour les effets (évite les re-fetch sur changement de référence).
  const orgIdsKey = useMemo(() => plateformes.map((o) => o.id).sort().join(','), [plateformes]);
  const selectedIdsKey = useMemo(() => [...selectedIds].sort().join(','), [selectedIds]);

  // Recherche des courtiers proposables, groupés par plateforme.
  useEffect(() => {
    if (plateformes.length === 0) {
      setResultsByOrg({});
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const entries = await Promise.all(
          plateformes.map(async (org) => {
            const response = await courtiersAPI.getAdminCourtiers({
              getGenericListCommandDto: {
                pageNumber: 1,
                pageSize: 10,
                searchString: search || undefined,
                filters: { plateformeIds: [org.id] },
              },
            });
            return [org.id, response.items ?? []] as const;
          }),
        );
        if (!cancelled) {
          setResultsByOrg(Object.fromEntries(entries));
        }
      } catch {
        if (!cancelled) {
          setResultsByOrg({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, orgIdsKey]);

  // Tunnels habilités des mandants retenus (un appel groupé, re-fetch quand la
  // sélection ou les plateformes changent).
  useEffect(() => {
    if (selectedIds.length === 0 || plateformes.length === 0) {
      setTunnelsByCourtier({});
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadingTunnels(true);
      try {
        const result = await getMandantsTunnels(
          selectedIds,
          plateformes.map((o) => o.id),
        );
        if (!cancelled) {
          setTunnelsByCourtier(result);
        }
      } catch {
        if (!cancelled) {
          setTunnelsByCourtier({});
        }
      } finally {
        if (!cancelled) {
          setLoadingTunnels(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey, orgIdsKey]);

  const toggleCourtier = useCallback(
    (courtier: CourtierListItem) => {
      const isSelected = selectedIds.includes(courtier.id);
      const next = isSelected
        ? selections.filter((s) => s.courtierId !== courtier.id)
        : [...selections, { courtierId: courtier.id, tunnelIds: [] }];

      setSelectedLabels((labels) => ({ ...labels, [courtier.id]: courtier.name }));
      helpers.setValue(next);
      helpers.setTouched(true);
    },
    [selections, selectedIds, helpers],
  );

  const removeCourtier = useCallback(
    (courtierId: string) => {
      helpers.setValue(selections.filter((s) => s.courtierId !== courtierId));
      helpers.setTouched(true);
    },
    [selections, helpers],
  );

  const toggleTunnel = useCallback(
    (courtierId: string, tunnelId: string) => {
      helpers.setValue(
        selections.map((s) => {
          if (s.courtierId !== courtierId) return s;
          const has = s.tunnelIds.includes(tunnelId);
          return {
            ...s,
            tunnelIds: has
              ? s.tunnelIds.filter((id) => id !== tunnelId)
              : [...s.tunnelIds, tunnelId],
          };
        }),
      );
      helpers.setTouched(true);
    },
    [selections, helpers],
  );

  if (plateformes.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
        Sélectionnez d&apos;abord la ou les plateformes : les courtiers
        mandants proposés en dépendent.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Mandants retenus : une carte par courtier avec SES tunnels accordés */}
      {selections.length > 0 && (
        <div className="flex flex-col gap-2">
          {selections.map((selection) => {
            const tunnels = tunnelsByCourtier[selection.courtierId] ?? [];
            return (
              <div
                key={selection.courtierId}
                className="rounded-lg border border-gray-200 bg-white"
              >
                <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
                  <span className="text-sm font-medium text-gray-900">
                    {selectedLabels[selection.courtierId] ?? selection.courtierId}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      <Route size={12} />
                      {selection.tunnelIds.length} tunnel{selection.tunnelIds.length > 1 ? 's' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeCourtier(selection.courtierId)}
                      className="text-gray-400 hover:text-red-600"
                      aria-label="Retirer le mandant"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                <div className="px-3 py-2">
                  {loadingTunnels && tunnels.length === 0 ? (
                    <p className="text-xs text-gray-400">Chargement des tunnels…</p>
                  ) : tunnels.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      Ce courtier n&apos;a aucun tunnel habilité dans les
                      plateformes choisies — aucun accès possible via lui.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {tunnels.map((tunnel) => {
                        const checked = selection.tunnelIds.includes(tunnel.tunnelId);
                        return (
                          <label
                            key={tunnel.tunnelId}
                            className={`flex items-center gap-2 rounded px-2 py-1 text-sm cursor-pointer hover:bg-gray-50 ${
                              checked ? 'bg-primary/5' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTunnel(selection.courtierId, tunnel.tunnelId)}
                              className="rounded border-gray-300"
                            />
                            <span className="flex min-w-0 flex-col">
                              <span className="truncate text-gray-900">{tunnel.label}</span>
                              <span className="truncate text-[11px] text-gray-400">
                                {tunnel.productLabel ? `${tunnel.productLabel} — ` : ''}
                                {tunnel.code}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {tunnels.length > 0 && selection.tunnelIds.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      Aucun tunnel coché : la liaison sera créée sans accès.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recherche + ajout de courtiers, groupés par plateforme */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un courtier mandant (raison sociale, SIRET...)"
          className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200">
        {loading && <div className="p-3 text-sm text-gray-500">Recherche…</div>}

        {!loading &&
          plateformes.map((org) => {
            const courtiers = resultsByOrg[org.id] ?? [];
            return (
              <div key={org.id} className="border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  <Building size={12} className="text-gray-400" />
                  {org.label}
                </div>
                {courtiers.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-400">Aucun courtier trouvé.</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {courtiers.map((courtier) => {
                      const isSelected = selectedIds.includes(courtier.id);
                      return (
                        <label
                          key={`${org.id}-${courtier.id}`}
                          className={`flex items-center gap-3 p-2.5 cursor-pointer text-sm hover:bg-gray-50 ${
                            isSelected ? 'bg-primary/5' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCourtier(courtier)}
                            className="rounded border-gray-300"
                          />
                          <span className="flex flex-col">
                            <span className="font-medium text-gray-900">{courtier.name}</span>
                            {courtier.siret && (
                              <span className="text-xs text-gray-400">SIRET: {courtier.siret}</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {meta.touched && meta.error && typeof meta.error === 'string' && (
        <p className="text-xs text-red-600">{meta.error}</p>
      )}
    </div>
  );
};
